import {
  GetUserNotificationByUserIdSubscription,
  useGetNotificationsByUserIdWithCountsLazyQuery,
  useGetUserNotificationByUserIdSubscription,
  useGetUserNotificationCountsByUserIdSubscription,
} from 'graphql/generated/hasura';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { ActivityFilter } from '../app/dashboard/components/provider/dashboardModules/activity/useProviderActivity';
import { AuthContext } from '../auth/context/AuthContext';
import { Roles } from '../firebase/interfaces';
import { ADMIN_ROLES } from '../utilities/constants';

const DEFAULT_LIMIT = 20;

type NotificationApi =
  GetUserNotificationByUserIdSubscription['user_notifications'][number];

export type Notification = Omit<NotificationApi, 'is_toast' | 'is_modal'> & {
  is_toast?: NotificationApi['is_toast'];
  is_modal?: NotificationApi['is_modal'];
  is_admin_notification?: boolean;
};

export type NotificationResult = {
  notifications: Notification[];
  total: number;
  unreadCount: number;
};

export interface useNotificationsResult {
  loading: boolean;
  notifications: NotificationResult | undefined;
  refetch: () => void;
  loadMoreNotifications: () => void;
  updateAllNotifications: () => Promise<void>;
  hasMore: boolean;
  unreadCount: number;
}

export function useNotifications(
  variables: ActivityFilter = { limit: DEFAULT_LIMIT, offset: 0 },
): useNotificationsResult {
  const { user: loggedUser, selectedAccount } = useContext(AuthContext);

  const [getNotifications] = useGetNotificationsByUserIdWithCountsLazyQuery();

  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [total, setTotal] = useState<number>(0);
  const [unreadCount, setUnreadCount] = useState<number>(0);
  const [offset, setOffset] = useState<number>(0);
  const [hasMore, setHasMore] = useState<boolean>(true);
  const [loading, setLoading] = useState<boolean>(false);
  const [isFetchingMore, setIsFetchingMore] = useState<boolean>(false);

  const isAdmin = useMemo(() => {
    return !!loggedUser?.role && ADMIN_ROLES.includes(loggedUser?.role);
  }, [loggedUser?.role]);

  const limit = variables.limit ?? DEFAULT_LIMIT;

  const updateAllNotifications = useCallback(async () => {
    const { data } = await getNotifications({
      variables: {
        ...variables,
        isLatestActivity: false,
        limit: limit + 1,
      },
      fetchPolicy: 'network-only',
    });

    const fetched =
      data?.getNotificationsByRequestUserIdWithCounts?.notifications ?? [];
    const total = data?.getNotificationsByRequestUserIdWithCounts?.total ?? 0;
    const unread =
      data?.getNotificationsByRequestUserIdWithCounts?.unreadCount ?? 0;

    const enriched = fetched.map((n) => ({
      ...n,
      user_id: loggedUser?.uuid,
    })) as Notification[];

    setNotifications(enriched);
    setTotal(total);
    setUnreadCount(unread);
  }, [getNotifications, limit, loggedUser?.uuid, variables]);

  const fetchNotifications = useCallback(
    async (offsetOverride = 0, append = false) => {
      if (append) setIsFetchingMore(true);
      else setLoading(true);

      try {
        const { data } = await getNotifications({
          variables: {
            ...variables,
            offset: offsetOverride,
            isLatestActivity: false,
            limit,
          },
          fetchPolicy: 'network-only',
        });

        const fetched =
          data?.getNotificationsByRequestUserIdWithCounts?.notifications ?? [];
        const total =
          data?.getNotificationsByRequestUserIdWithCounts?.total ?? 0;
        const unread =
          data?.getNotificationsByRequestUserIdWithCounts?.unreadCount ?? 0;

        const enriched = fetched.map((n) => ({
          ...n,
          user_id: selectedAccount || loggedUser?.uuid,
        })) as Notification[];
        setNotifications((prev) =>
          append ? [...prev, ...enriched] : enriched,
        );
        setTotal(total);
        setUnreadCount(unread);
        setHasMore(offsetOverride + limit < total);
        if (append) setOffset(offsetOverride);
      } catch (error) {
        console.error('Failed to fetch notifications:', error);
      } finally {
        setLoading(false);
        setIsFetchingMore(false);
      }
    },
    [getNotifications, variables, limit, selectedAccount, loggedUser?.uuid],
  );

  // Initial load
  useEffect(() => {
    fetchNotifications(0, false);
    setOffset(0);
  }, [fetchNotifications]);

  const refetch = useCallback(() => {
    fetchNotifications(0, false);
    setOffset(0);
  }, [fetchNotifications]);

  const loadMoreNotifications = useCallback(() => {
    if (loading || isFetchingMore || !hasMore) return;
    const nextOffset = offset + limit;
    fetchNotifications(nextOffset, true);
  }, [offset, hasMore, loading, isFetchingMore, fetchNotifications, limit]);

  // Live socket updates
  const where: Record<string, unknown> = {
    status: { _in: variables.includeStatuses },
    has_priority: { _in: variables.includePriorities },
    activity_type: { _in: variables.activityTypes },
    user_id: variables.userId ? { _eq: variables.userId } : undefined,
    is_toast: { _eq: false },
    is_important_update: { _eq: false },
    is_latest_activity:
      loggedUser?.role === Roles.PROVIDER ? { _eq: false } : undefined,
    is_admin_notification: ADMIN_ROLES.includes(loggedUser?.role as Roles)
      ? {
          _eq: true,
        }
      : undefined,
  };

  useGetUserNotificationByUserIdSubscription({
    variables: {
      where,
      limit: (variables?.limit || 0) + offset,
    },
    fetchPolicy: 'network-only',
    onData: ({ data }) => {
      const notifications = data.data?.user_notifications ?? [];
      setNotifications(() => notifications);
    },
  });

  useGetUserNotificationCountsByUserIdSubscription({
    variables: {
      where,
    },
    fetchPolicy: 'network-only',
    onData: ({ data }) => {
      const total =
        data.data?.user_notifications_aggregate?.aggregate?.count ?? 0;
      setTotal(total);
    },
  });

  const result = useMemo<useNotificationsResult>(
    () => ({
      refetch,
      loadMoreNotifications,
      unreadCount,
      hasMore,
      loading,
      updateAllNotifications,
      notifications: notifications.length
        ? {
            notifications: notifications.filter(
              ({ is_admin_notification }) =>
                isAdmin === !!is_admin_notification,
            ),
            total,
            unreadCount,
          }
        : undefined,
    }),
    [
      isAdmin,
      hasMore,
      loadMoreNotifications,
      notifications,
      loading,
      refetch,
      total,
      unreadCount,
      updateAllNotifications,
    ],
  );

  return result;
}
